import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ArrowUpRight } from "lucide-react";
import { Loading } from "@/components/Loading";
import { Dash } from "@/components/Dash";
import { Link, useNavigate } from "react-router-dom";
import { useBackOrigin } from "@/hooks/useBackTo";
import { useUrlParam } from "@/hooks/useUrlParam";
import { ProcessorEmailsPanel } from "@/features/applications/components/ProcessorEmailsPanel";
import { DuplicatesPanel } from "@/features/applications/components/DuplicatesPanel";
import { toast } from "sonner";
import { applicationsV2, processorEmails, SUBMITTED_GROUP, ESIGNED_GROUP, DONE_GROUP, WAITING_ON_AGENT, IN_PROGRESS, type ApplicationV2ListItem, type ApplicationV2Summary } from "@/features/applications/api/applications.api";
import { waitingOn, timeInStatus, useMinuteTick, NEXT_STEP } from "@/features/applications/components/v2";
import { useAuth } from "@/features/auth/auth";
import { equipment } from "@/features/equipment/api/equipment.api";
import { FEATURES } from "@/config/featureFlags";
import { usePagedList } from "@/hooks/usePagedList";
import { Pagination } from "@/components/Pagination";
import { Notice } from "@/components/Notice";
import { StatusPill, APPLICATION_STATUS_TONES, APPLICATION_STATUS_LABELS } from "@/components/StatusPill";
import { SortableTH } from "@/components/SortableTH";
import { useColumnSort } from "@/hooks/useColumnSort";
import { FilterBar } from "@/components/FilterBar";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/utils/dates";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type Filter = "pricing" | "submitted" | "esignSent" | "esigned" | "pended" | "done" | "approved" | "boarded" | "live" | "drafts" | "all" | "you" | "progress" | "duplicates";

const FILTER_STATUS: Record<Filter, string | undefined> = {
  all: undefined,
  pricing: "AwaitingAgentPricing",
  submitted: SUBMITTED_GROUP,
  esignSent: "ESignSent",
  esigned: ESIGNED_GROUP,
  pended: "ActionNeeded",
  done: DONE_GROUP,
  approved: "Approved",
  boarded: "Boarded",
  live: "Live",
  drafts: "Draft",
  you: WAITING_ON_AGENT,
  progress: IN_PROGRESS,
  duplicates: undefined,
};

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "pricing", label: "Needs pricing" },
  { id: "submitted", label: "Submitted" },
  { id: "esignSent", label: "E-sign sent" },
  { id: "esigned", label: "E-signed" },
  { id: "pended", label: "Pended" },
  { id: "done", label: "Approved · Boarded · Live" },
  { id: "drafts", label: "Drafts" },
  { id: "duplicates", label: "Duplicates" },
];

const AGENT_FILTERS: { id: Filter; label: string }[] = [
  { id: "you", label: "Waiting on you" },
  { id: "progress", label: "In progress" },
  { id: "live", label: "Live" },
  { id: "drafts", label: "Drafts" },
];

const FILTER_IDS = [...FILTERS, ...AGENT_FILTERS].map((f) => f.id);
const PANELS = ["list", "emails"] as const;
const PUSH_HISTORY = { push: true };

const FILTER_COUNT: Record<Filter, (s: ApplicationV2Summary) => number> = {
  all: (s) => s.total,
  pricing: (s) => s.awaitingAgentPricing ?? 0,
  submitted: (s) => (s.needsReview === undefined ? s.submitted : s.needsReview - (s.awaitingAgentPricing ?? 0)),
  esignSent: (s) => s.eSignSent ?? 0,
  esigned: (s) => s.eSigned ?? 0,
  pended: (s) => s.uwPended ?? 0,
  done: (s) => s.approved + s.boarded + (s.live ?? 0),
  approved: (s) => s.approved,
  boarded: (s) => s.boarded,
  live: (s) => s.live ?? 0,
  you: (s) => s.waitingOnAgent ?? 0,
  progress: (s) => s.inProgress ?? 0,
  drafts: (s) => s.draft,
  duplicates: () => 0,
};

type SortKey = "business" | "status" | "received";

const COLUMN_MAP: Record<SortKey, string> = {
  business: "dbaName",
  status: "status",
  received: "receivedAt",
};

const EQUIPMENT_HIDDEN = new Set(["Draft", "Saved", "Declined", "Cancelled", "Merged"]);

export default function ApplicationsV2Page() {
  const navigate = useNavigate();
  const origin = useBackOrigin();
  const now = useMinuteTick();
  const [creating, setCreating] = useState(false);
  const { user } = useAuth();
  const [summary, setSummary] = useState<ApplicationV2Summary | null>(null);
  const isAgent = summary?.viewerIsBackOffice === undefined ? user?.role === "USER" : !summary.viewerIsBackOffice;
  const [filter, setFilter] = useUrlParam<Filter>("view", isAgent ? "you" : "submitted", FILTER_IDS);
  const [panel, setPanel] = useUrlParam<"list" | "emails">("panel", "list", PANELS, PUSH_HISTORY);
  const [emailsAttention, setEmailsAttention] = useState<number | null>(null);
  const [duplicateCount, setDuplicateCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    processorEmails.list(true, 1, 1)
      .then((d) => { if (!cancelled) setEmailsAttention(d.needsAttention); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (isAgent) return;
    let cancelled = false;
    applicationsV2.duplicates()
      .then((p) => { if (!cancelled) setDuplicateCount(p.length); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAgent]);

  useEffect(() => {
    let cancelled = false;
    applicationsV2.summary()
      .then((s) => { if (!cancelled) setSummary(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const extra = useMemo(() => ({ status: FILTER_STATUS[filter] }), [filter]);
  const { data, loading, loaded, error, forbidden, keyword, sortBy, sortDir, setKeyword, setPage, setSort } =
    usePagedList<ApplicationV2ListItem>(applicationsV2.list, {
      extra,
      initialSortBy: "receivedAt",
      initialSortDir: "desc",
      syncUrl: true,
    });
  const filtered = data.items;
  const equipmentIds = filtered.map((a) => a.id).join(",");
  const [equipmentDone, setEquipmentDone] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!equipmentIds || !FEATURES.equipmentForAgents) return;
    let cancelled = false;
    equipment
      .applicationStatus(equipmentIds.split(","))
      .then((rows) => !cancelled && setEquipmentDone(Object.fromEntries(rows.map((r) => [r.applicationId, r.done || !r.tracked]))))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [equipmentIds]);
  const { thProps } = useColumnSort<SortKey>(COLUMN_MAP, sortBy, sortDir, setSort);
  const [createError, setCreateError] = useState<string | null>(null);

  async function createNew() {
    setCreating(true);
    setCreateError(null);
    try {
      const app = await applicationsV2.create();
      toast.success("Application created");
      navigate(`/applications/v2/${app.id}`, { state: origin });
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Couldn't create an application.");
      setCreating(false);
    }
  }

  return (
    <>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            Applications
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Boarding pipeline — onboarding form submissions land here for
            review, approve, and board-as-merchant.
          </p>
        </div>
        {!forbidden && (
          <div className="flex items-center gap-2">
            {emailsAttention !== null && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setPanel(panel === "emails" ? "list" : "emails")}
                className="whitespace-nowrap"
              >
                {panel === "emails" ? "← Applications" : "TSYS updates"}
                {panel !== "emails" && emailsAttention > 0 && (
                  <span className="ml-2 rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white" aria-label={`${emailsAttention} need a person`}>{emailsAttention}</span>
                )}
              </Button>
            )}
            {user?.role === "ADMIN" && (
              <Button type="button" variant="outline" asChild className="whitespace-nowrap">
                <Link to="/applications/v2/bank-check-audit">Bank check audit</Link>
              </Button>
            )}
            <Button
              type="button"
              onClick={createNew}
              disabled={creating}
              className="whitespace-nowrap"
            >
              {creating ? "Creating…" : "New application"}
            </Button>
          </div>
        )}
      </header>

      {!forbidden && panel === "emails" && <ProcessorEmailsPanel onCount={setEmailsAttention} />}

      {forbidden && (
        <Notice tone="warn">
          You don't have permission to view boarding applications. Ask an
          admin to grant <code>30.03</code> on your role.
        </Notice>
      )}

      {(error || createError) && !forbidden && (
        <Notice tone="error" className="mb-6">{error ?? createError}</Notice>
      )}

      {!forbidden && !error && panel === "list" && (
        <>
          <FilterBar
            tabs={(isAgent ? AGENT_FILTERS : FILTERS).map((f) => ({
              value: f.id,
              label: f.label,
              count: f.id === "duplicates" ? (duplicateCount ?? undefined) : summary ? FILTER_COUNT[f.id](summary) : undefined,
            }))}
            value={filter}
            onValueChange={(v) => setFilter(v as Filter)}
            search={keyword}
            onSearchChange={setKeyword}
            searchPlaceholder="Search business, contact, or email…"
          />

          {filter === "duplicates" && !isAgent ? (
            <DuplicatesPanel onCount={setDuplicateCount} />
          ) : (<>
          {loading && !loaded && <Loading>Loading applications…</Loading>}

          <div className={cn("transition-opacity", loading && loaded && "opacity-60")}>
          {!isAgent && filtered.some((a) => a.rateApprovalPending) && (
            <Notice tone="warn" className="mb-3 px-3 py-2">
              ⏳ {filtered.filter((a) => a.rateApprovalPending).length} rate exception
              {filtered.filter((a) => a.rateApprovalPending).length === 1 ? " is" : "s are"} waiting for an admin. Open the application to approve or decline.
            </Notice>
          )}
          {filtered.length === 0 ? (
            loaded && (
              <Notice tone="info">
                No applications match this filter. Click "New application" to start one, or share the onboarding form link.
              </Notice>
            )
          ) : (
            <div className="rounded-md border bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTH {...thProps("business")}>Business</SortableTH>
                    <SortableTH {...thProps("status")}>Status</SortableTH>
                    <Th>{isAgent && filter === "you" ? "Next step" : "Waiting on"}</Th>
                    <Th className="hidden sm:table-cell">In status</Th>
                    {FEATURES.equipment && <Th className="hidden md:table-cell">Equipment</Th>}
                    <Th className="hidden md:table-cell">Owner</Th>
                    <SortableTH {...thProps("received")}>Received</SortableTH>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((a) => (
                    <TableRow
                      key={a.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/applications/v2/${a.id}`, { state: origin })}
                    >
                      <TableCell>
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <Link
                              to={`/applications/v2/${a.id}`}
                              state={origin}
                              className="font-medium text-slate-900 hover:underline"
                              title={a.legalName && a.legalName !== a.dbaName ? `Legal name: ${a.legalName}` : undefined}
                            >
                              {a.dbaName || a.legalName || "(untitled application)"}
                            </Link>
                            {a.rateApprovalPending && (
                              <span className="mt-1 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-300">
                                Needs rate approval
                              </span>
                            )}
                          </div>

                        </div>
                      </TableCell>
                      <TableCell>
                        {(a.status === "Boarded" || a.status === "Live") && a.merchantId ? (
                          <Link
                            to={`/merchants/${a.merchantId}`}
                            state={origin}
                            onClick={(e) => e.stopPropagation()}
                            title={a.processorMid ? `Open merchant · MID ${a.processorMid}` : "Open merchant"}
                            aria-label={`${APPLICATION_STATUS_LABELS[a.status] ?? a.status}, open merchant`}
                            className="rounded-full transition-opacity hover:opacity-80"
                          >
                            <StatusPill
                              value={a.status}
                              map={APPLICATION_STATUS_TONES}
                              labels={APPLICATION_STATUS_LABELS}
                              suffix={<ArrowUpRight className="h-3 w-3" aria-hidden="true" />}
                            />
                          </Link>
                        ) : (
                          <StatusPill value={a.status} map={APPLICATION_STATUS_TONES} labels={APPLICATION_STATUS_LABELS} />
                        )}
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {isAgent && NEXT_STEP[a.status] ? (
                          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900">{NEXT_STEP[a.status]} →</span>
                        ) : FEATURES.equipmentForAgents && isAgent && filter === "you" && equipmentDone[a.id] === false && !EQUIPMENT_HIDDEN.has(a.status) && a.status !== "Live" ? (
                          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900">Order equipment →</span>
                        ) : a.rateApprovalPending && !isAgent ? "Admin (rate approval)" : (waitingOn(a.status, a.owner?.name) ?? <Dash />)}
                      </TableCell>
                      <TableCell className="hidden whitespace-nowrap text-slate-600 sm:table-cell">
                        {timeInStatus(a.statusSince, now) ?? <Dash />}
                      </TableCell>
                      {FEATURES.equipment && (
                      <TableCell className="hidden md:table-cell">
                        {EQUIPMENT_HIDDEN.has(a.status) || !(a.id in equipmentDone) ? (
                          <Dash />
                        ) : equipmentDone[a.id] ? (
                          <StatusPill value="Ordered" fallback="success" />
                        ) : (
                          <StatusPill value="Not ordered" fallback="warning" />
                        )}
                      </TableCell>
                      )}
                      <TableCell className="hidden text-slate-700 md:table-cell">
                        {a.owner?.name || <Dash />}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-600 text-xs">
                        {a.status === "Draft" || a.status === "Saved" ? (
                          <span className="text-slate-400" title={formatDateTime(a.updatedAt)}>
                            {a.updatedAt ? `Draft · ${relativeAgo(a.updatedAt)}` : "Draft"}
                          </span>
                        ) : (
                          <span title={formatDateTime(a.submittedAt || a.updatedAt)}>
                            {relativeAgo(a.submittedAt || a.updatedAt)}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination page={data} onPageChange={setPage} busy={loading} />
            </div>
          )}
          </div>
          </>)}
        </>
      )}
    </>
  );
}

function relativeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diffMs = Date.now() - t;
  const diffSec = Math.max(1, Math.round(diffMs / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMo = Math.round(diffDay / 30);
  if (diffMo < 12) return `${diffMo}mo ago`;
  return formatDateTime(iso, { year: "numeric", month: "short", day: "numeric" });
}

function Th({ children, align = "left", className }: { children: ReactNode; align?: "left" | "right"; className?: string }) {
  return (
    <TableHead className={cn("text-xs font-medium uppercase tracking-wide", align === "right" && "text-right", className)}>
      {children}
    </TableHead>
  );
}

